/* eslint-disable jsx-a11y/anchor-is-valid */
import {
    Button,
    Col,
    Collapse,
    Dropdown,
    Input,
    InputNumber,
    Menu,
    Row,
    Select,
    Slider,
    Switch,
    Typography
} from 'antd'
import * as THREE from 'three'
import React, { useState } from 'react'
import { ChromePicker } from 'react-color'
import CollapsePanel from 'antd/lib/collapse/CollapsePanel'

import { checkTypeValue } from '../../../utils'
import styled from 'styled-components'
import RenderAttachTexture from './editor3D/renderAttachTexture'
import RenderAttachAsset from './editor3D/renderAttachAsset'

export type TAssetEditorProp = {
    key: string
    //TODO checks
    value: any
    label: string
    type:
        | 'input'
        | 'switch'
        | 'range'
        | 'list'
        | 'color'
        | 'tuple'
        | 'button'
        | 'attach'
        | 'attachTexture'
        | 'select'
        | 'vec2'
    misc?: {
        maxLength?: number
        precision?: number
        min?: number
        max?: number
        list?: Array<{
            label: string
            key: string
            value: string | number
        }>
    }
}

export type TAssetEditorSection = {
    name: string
    props: TAssetEditorProp[]
}

export type TAssetEditorProps = {
    sectionsList: TAssetEditorSection[]
    onPropChange: (key: string, value: TAssetEditorProp['value']) => void
    asset?: any
}

const AssetEditorProps: React.FC<TAssetEditorProps> = ({ sectionsList, onPropChange, asset }) => {
    const renderInput = (prop: TAssetEditorProp) => {
        const maxLength = prop.misc?.maxLength || 100
        if (!checkTypeValue<string>(prop.value)) return
        return (
            <PropsItem span={16}>
                <Input
                    maxLength={maxLength}
                    value={prop.value}
                    onChange={e => {
                        onPropChange(prop.key, e.target.value)
                    }}
                />
            </PropsItem>
        )
    }

    const renderSwitch = (prop: TAssetEditorProp) => {
        return (
            <PropsItem span={16}>
                <Switch
                    checked={prop.value}
                    onChange={value => {
                        onPropChange(prop.key, value)
                    }}
                />
            </PropsItem>
        )
    }
    const renderButton = (prop: TAssetEditorProp) => {
        if (!checkTypeValue<string>(prop.value)) return

        return (
            <PropsItem span={16}>
                <Button
                    onClick={() => {
                        onPropChange(prop.key, '')
                    }}>
                    {prop.value}
                </Button>
            </PropsItem>
        )
    }

    const renderRange = (prop: TAssetEditorProp) => {
        const precision = prop.misc?.precision || 0.01
        const min = prop.misc?.min || 0
        const max = prop.misc?.max || 100

        return (
            <>
                <PropsItem span={5}>
                    <InputNumber
                        min={0}
                        max={1}
                        step={0.01}
                        value={prop.value}
                        onChange={value => {
                            onPropChange(prop.key, value as number)
                        }}
                    />
                </PropsItem>
                <PropsItem span={11}>
                    <Slider
                        style={{ flex: 1 }}
                        min={min}
                        max={max}
                        step={precision}
                        value={prop.value}
                        onChange={value => {
                            onPropChange(prop.key, value)
                        }}
                    />
                </PropsItem>
            </>
        )
    }

    const RenderColor = (prop: TAssetEditorProp) => {
        const [pickerShown, setPickerShown] = useState(false)
        let newValue = prop.value as string

        if (prop.value instanceof THREE.Color) {
            newValue = prop.value.getStyle()
        }
        return (
            <div style={{ position: 'relative', display: 'flex', flex: 1, gap: '40px' }}>
                <PropsItem span={6}>
                    <ColorIcon backgroundColor={newValue} onClick={() => setPickerShown(!pickerShown)} />
                </PropsItem>
                {pickerShown ? (
                    <div style={{ zIndex: 9999, position: 'absolute', top: 0, right: '50%' }}>
                        <div
                            style={{
                                position: 'fixed',
                                top: '0px',
                                right: '0px',
                                bottom: '0px',
                                left: '0px'
                            }}
                            onClick={() => setPickerShown(false)}
                        />
                        <ChromePicker
                            color={newValue}
                            onChange={color => {
                                const {
                                    rgb: { r, g, b, a }
                                } = color
                                onPropChange(prop.key, `rgba(${r}, ${g}, ${b}, ${a})`)
                            }}
                        />
                    </div>
                ) : null}
            </div>
        )
    }

    const renderList = (prop: TAssetEditorProp) => {
        const list = prop.misc!.list!

        const MenuItems = () => {
            return (
                <Menu>
                    {list.map(item => {
                        return (
                            <Menu.Item
                                key={item.key}
                                onClick={e => {
                                    onPropChange(prop.key, item.key)
                                }}>
                                {item.label}
                            </Menu.Item>
                        )
                    })}
                </Menu>
            )
        }

        return (
            <Dropdown overlay={MenuItems}>
                <PropsItem>
                    <a href={'#'}>{prop.value}</a>
                </PropsItem>
            </Dropdown>
        )
    }

    const renderSelect = (prop: TAssetEditorProp) => {
        return (
            <PropsItem span={16}>
                <SelectItem
                    options={prop.value.options}
                    onChange={event => {
                        onPropChange(prop.key, { object: event })
                    }}
                    placeholder="Select..."
                    value={prop.value.value}
                />
            </PropsItem>
        )
    }

    const renderTupleValue = (prop: TAssetEditorProp) => {
        const maxLength = prop.misc?.maxLength || 100

        if (!checkTypeValue<THREE.Vector3 | THREE.Euler>(prop.value)) return
        return (
            <Col span={16}>
                <Row>
                    <PropsItem span={8}>
                        <Input
                            maxLength={maxLength}
                            value={prop.value.x.toFixed(2)}
                            onChange={e => {
                                console.log(+e.target.value)
                                if (!checkTypeValue<THREE.Vector3 | THREE.Euler>(prop.value)) return
                                if ('isEuler' in prop.value) {
                                    onPropChange(prop.key, new THREE.Euler(+e.target.value, prop.value.y, prop.value.z))
                                    return
                                }
                                onPropChange(prop.key, new THREE.Vector3(+e.target.value, prop.value.y, prop.value.z))
                            }}
                        />
                    </PropsItem>
                    <PropsItem span={8}>
                        <Input
                            maxLength={maxLength}
                            value={prop.value.y.toFixed(2)}
                            onChange={e => {
                                if (!checkTypeValue<THREE.Vector3 | THREE.Euler>(prop.value)) return

                                if ('isEuler' in prop.value) {
                                    onPropChange(prop.key, new THREE.Euler(prop.value.x, +e.target.value, prop.value.z))
                                    return
                                }
                                onPropChange(prop.key, new THREE.Vector3(prop.value.x, +e.target.value, prop.value.z))
                            }}
                        />
                    </PropsItem>
                    <PropsItem span={8}>
                        <Input
                            maxLength={maxLength}
                            value={prop.value.z.toFixed(2)}
                            onChange={e => {
                                if (!checkTypeValue<THREE.Vector3 | THREE.Euler>(prop.value)) return
                                if ('isEuler' in prop.value) {
                                    onPropChange(prop.key, new THREE.Euler(prop.value.x, prop.value.y, +e.target.value))
                                    return
                                }
                                onPropChange(prop.key, new THREE.Vector3(prop.value.x, prop.value.y, +e.target.value))
                            }}
                        />
                    </PropsItem>
                </Row>
            </Col>
        )
    }
    const renderVec2Value = (prop: TAssetEditorProp) => {
        const maxLength = prop.misc?.maxLength || 100

        return (
            <Col span={16}>
                <Row>
                    <PropsItem span={8}>
                        <Input
                            maxLength={maxLength}
                            value={prop.value.x.toFixed(2)}
                            onChange={e => {
                                onPropChange(prop.key, new THREE.Vector2(+e.target.value, prop.value.y))
                            }}
                        />
                    </PropsItem>
                    <PropsItem span={8}>
                        <Input
                            maxLength={maxLength}
                            value={prop.value.y.toFixed(2)}
                            onChange={e => {
                                onPropChange(prop.key, new THREE.Vector2(prop.value.x, +e.target.value))
                            }}
                        />
                    </PropsItem>
                </Row>
            </Col>
        )
    }

    return (
        <WrapperProps className={'viewer_props'}>
            <Typography.Title level={3}>Properties</Typography.Title>
            {sectionsList.length ? (
                <Collapse defaultActiveKey={sectionsList[0].name}>
                    {sectionsList.map(section => {
                        const { name, props } = section
                        return (
                            <CollapsePanel style={{ fontSize: '12px' }} header={name || ''} key={section.name}>
                                {props.map(prop => {
                                    return (
                                        <Row key={prop.key} style={{ height: 44 }}>
                                            <PropsItem span={8}>
                                                <span>{prop.label}</span>
                                            </PropsItem>
                                            {(() => {
                                                if (prop.type === 'input') {
                                                    return renderInput(prop)
                                                }

                                                if (prop.type === 'switch') {
                                                    return renderSwitch(prop)
                                                }

                                                if (prop.type === 'range') {
                                                    return renderRange(prop)
                                                }

                                                if (prop.type === 'list') {
                                                    return renderList(prop)
                                                }

                                                if (prop.type === 'color') {
                                                    return RenderColor(prop)
                                                }
                                                if (prop.type === 'tuple') {
                                                    return renderTupleValue(prop)
                                                }
                                                if (prop.type === 'vec2') {
                                                    return renderVec2Value(prop)
                                                }
                                                if (prop.type === 'button') {
                                                    return renderButton(prop)
                                                }
                                                if (prop.type === 'attach') {
                                                    return (
                                                        <RenderAttachAsset
                                                            asset={asset}
                                                            keyMap={prop.key}
                                                            onPropChange={onPropChange}
                                                            value={prop.value}
                                                        />
                                                    )
                                                }
                                                if (prop.type === 'attachTexture') {
                                                    return (
                                                        <RenderAttachTexture
                                                            asset={asset}
                                                            keyMap={prop.key}
                                                            onPropChange={onPropChange}
                                                            value={prop.value}
                                                        />
                                                    )
                                                }
                                                if (prop.type === 'select') {
                                                    return renderSelect(prop)
                                                }
                                                return null
                                            })()}
                                        </Row>
                                    )
                                })}
                            </CollapsePanel>
                        )
                    })}
                </Collapse>
            ) : null}
        </WrapperProps>
    )
}

export default AssetEditorProps

const WrapperProps = styled.div`
    height: 100%;
    overflow: auto;
    padding: 10px;
`

const PropsItem = styled(Col)`
    display: flex;
    align-items: center;
    gap: 20px;
`

const DefaultIcon = styled.div`
    width: 60px;
    height: 30px;
    border: 1px solid #000;
`

const ColorIcon = styled(DefaultIcon)<{ backgroundColor: string }>`
    background: ${props => `${props?.backgroundColor} !important`};
`

const SelectItem = styled(Select)`
    width: 100%;
`
